import { Formik } from 'formik'
import { debounce, isEqual, map } from 'lodash'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { defaultMemoize } from 'reselect'

import Card from '@/components/Card'
import DeleteConfirmationOverlay from '@/components/DeleteConfirmationOverlay'
import { AutoComplete } from '@/components/Form'
import { Person } from '@/components/Icons'
import PaginationTable from '@/components/PaginationTable'
import { FETCH_ORG_ADMINS_TO_ADD } from '@/pages/OrganizationSpaceEditPage/actionTypes'

import columns from './columns'
import messages from './messages'

import './index.scss'

import { usePrevious } from 'react-use'

import { USER_ATTRIBUTES } from '@/components/User'

/**
 * Prevent unnecessary network requests to fetch resolved targeting rules and attribute suggestions by waiting for
 * further user input for this amount of time before fetching. This technique is called debouncing
 *
 * @type {number} time to delay in milliseconds
 */
const SEARCH_DELAY_MS = 300

function SpaceAdmins(props: any) {
  const intl = useIntl()

  const [state, _setState] = useState({
    currentPage: 1,
    searchTerm: '',
    isSpaceNameValid: true,
    searchResults: [] as any[],
    userToRemove: undefined as { id: any; roles: any } | undefined,
    isDeleting: undefined as boolean | undefined
  })
  const setState = (update: Partial<Parameters<typeof _setState>[0]>) => {
    return _setState(state => ({ ...state, ...(typeof update === 'function' ? update(state) : update) }))
  }

  // componentDidUpdate
  const prevProps = usePrevious(props)
  useEffect(() => {
    if (!prevProps) return

    // Compare both the data and the overall status.
    // We want to remove results from the dropdown when the request is in progress
    if (
      (props.isSearchUsersComplete && !prevProps.isSearchUsersComplete) ||
      !isEqual(props.searchResults, prevProps.searchResults)
    ) {
      setState({
        searchResults: props.searchResults
      })
    }
  }, [prevProps, props])

  const addSpaceAdminRole = async (user: any) => {
    const { addUserToSpaceAsAdmin, addSpaceAdmin, space, spaceAdminRole } = props

    const existingRoles = user.roles.spaces[space.id]
    if (!existingRoles) {
      await addUserToSpaceAsAdmin(space.id, user, spaceAdminRole.id)
    } else if (!user.roles.spaces[space.id].map((userRole: any) => userRole.id).includes(spaceAdminRole.id)) {
      existingRoles.push(spaceAdminRole)
      addSpaceAdmin(
        space.id,
        user.id,
        existingRoles.map((r: any) => r.id)
      )
    }
  }

  const fetchUserSearchResults = debounce(filter => {
    const query: { attributes: string[]; count: number; filter?: string } = {
      attributes: USER_ATTRIBUTES,
      count: 10
    }

    if (filter) {
      query.filter = ['userName', 'name.formatted', 'name.familyName', 'name.givenName', 'displayName']
        .map(attribute => `${attribute} co ${JSON.stringify(filter)}`)
        .join(' or ')
    }

    props.fetchUsersPaginated(query, FETCH_ORG_ADMINS_TO_ADD)
  }, SEARCH_DELAY_MS)

  const generateSearchResults = defaultMemoize((searchTerm, users) => {
    if (searchTerm.length === 0) {
      return []
    }

    return map(users, user => ({
      label: user.displayName,
      leftIcon: <Person />,
      subLabel: user.email,
      value: user.id,
      id: user.id
    }))
  })

  const getActionMessage = () => {
    if (props.isAddingSpaceAdmin) {
      return <FormattedMessage {...messages.addingRole} />
    } else if (props.isRemovingSpaceAdmin) {
      return <FormattedMessage {...messages.removingRole} />
    }
  }

  const onSearchChange = (value: any) => {
    value = value.trim()

    setState({ searchTerm: value })

    // Don't search if the user enters in spaces
    if (value.length === 0) {
      return
    }

    return fetchUserSearchResults(value)
  }

  const onSearchSelect = (value: any) => {
    // Reset the input to the search box
    setState({ searchTerm: '' })

    addSpaceAdminRole(state.searchResults.find(user => user.id === value.id))

    return { display: '', value: '' }
  }

  const renderItem = ({ label, leftIcon, subLabel, value }: any) => {
    return (
      <div key={value} className="space-member-result">
        {!!leftIcon && <div className="result__left">{leftIcon}</div>}
        <div className="result__right">
          <p>{label}</p>
          {!!subLabel && <p>{subLabel}</p>}
        </div>
      </div>
    )
  }

  const removeSpaceAdminRole = () => {
    const { space, spaceAdminRole } = props
    const { userToRemove } = state

    setState({
      isDeleting: false
    })

    try {
      if (userToRemove) {
        const updatedRoles = userToRemove.roles.spaces[space.id].filter((role: any) => role.id !== spaceAdminRole.id)
        props.removeSpaceAdmin(space.id, userToRemove.id, updatedRoles)
      }
    } finally {
      setState({
        userToRemove: null
      })
    }
  }

  const showConfirmDelete = (user: any) => {
    setState({ isDeleting: true, userToRemove: user })
  }

  const onCancelDelete = () => {
    setState({ isDeleting: false, userToRemove: '' })
  }

  const onSelect = (page: any) => {
    setState({ currentPage: page })
  }

  return (
    <Card
      isActionInProgress={props.isAddingSpaceAdmin || props.isFetchingAdmins || props.isRemovingSpaceAdmin}
      actionMessage={getActionMessage()}
    >
      <Card.Header title={<FormattedMessage {...messages.spaceAdministrators} />} />

      <Card.Content>
        {/* @ts-expect-error */}
        <Formik initialValues={{ search: state.searchTerm }}>
          <AutoComplete
            className="admin-search_autocomplete"
            key="spaceAdmin"
            name="spaceAdmin"
            label={<FormattedMessage {...messages.addAdministrator} />}
            placeholder={intl.formatMessage(messages.searchPlaceholder)}
            onResultClick={onSearchSelect}
            onSearchChange={onSearchChange}
            renderResult={renderItem}
            isSearching={props.isSearching}
            results={generateSearchResults(state.searchTerm, state.searchResults)}
          />
        </Formik>

        {props.spaceAdmins.length > 0 && (
          <PaginationTable
            columns={columns({ intl: intl, showConfirmDelete: showConfirmDelete })}
            pagination={{
              isClientPaging: true,
              startIndex: state.currentPage * 10,
              count: props.spaceAdmins.length,
              perPage: 10
            }}
            onPaginateSelect={onSelect}
            rows={props.spaceAdmins}
            rowItemType={<FormattedMessage {...messages.users} />}
            rowKey="id"
            sortingColumn={props.sortingColumn}
          />
        )}

        {props.spaceAdmins <= 0 && !props.isAddingSpaceAdmin && (
          <p className="no-users flex-center">
            <FormattedMessage {...messages.noAdmins} />
          </p>
        )}
      </Card.Content>

      {!!state.isDeleting && (
        <DeleteConfirmationOverlay
          onConfirm={removeSpaceAdminRole}
          onCancel={onCancelDelete}
          message={<FormattedMessage {...messages.confirmRemove} />}
          subMessage={<FormattedMessage {...messages.confirmSpaceAdminRemove} />}
        />
      )}
    </Card>
  )
}

SpaceAdmins.displayName = 'SpaceAdmins'

SpaceAdmins.propTypes = {
  addSpaceAdmin: PropTypes.func.isRequired,
  addUserToSpaceAsAdmin: PropTypes.func.isRequired,
  fetchUsersPaginated: PropTypes.func.isRequired,

  isSearching: PropTypes.bool.isRequired,
  isFetchingAdmins: PropTypes.bool.isRequired,
  isAddingSpaceAdmin: PropTypes.bool.isRequired,
  isRemovingSpaceAdmin: PropTypes.bool.isRequired,
  isSearchUsersComplete: PropTypes.bool.isRequired,
  removeSpaceAdmin: PropTypes.func.isRequired,
  searchResults: PropTypes.arrayOf(PropTypes.object),
  sortingColumn: PropTypes.object,
  space: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    default: PropTypes.bool
  }),
  spaceAdminRole: PropTypes.object,
  spaceAdmins: PropTypes.arrayOf(PropTypes.object)
}

SpaceAdmins.defaultProps = {
  searchResults: null,
  sortingColumn: { name: { direction: 'asc', position: 1 } },
  space: {},
  spaceAdminRole: {},
  spaceAdmins: []
}

export default SpaceAdmins
